<template>
    <div class="container">
        <h1>{{$t('referrallead.form.title')}}</h1>

        <form @submit.prevent="handleSubmit">
            <section>
                <h2 class="section-title">{{$t('referrallead.form.writer.header')}}</h2>

                <div class="form-group">
                    <label for="writerCompanyName">{{$t('referrallead.form.writer.company.name')}}</label>
                    <input v-model="vm.form.writerCompanyName" id="writerCompanyName" type="text" required />
                </div>

                <div class="form-group">
                    <label for="writerName">{{$t('referrallead.form.writer.name')}}</label>
                    <input v-model="vm.form.writerName" id="writerName" type="text" required />
                </div>

                <div class="form-group">
                    <label for="writerEmail">{{$t('referrallead.form.writer.email')}}</label>
                    <input v-model="vm.form.writerEmail" id="writerEmail" type="email" required />
                </div>

                <div class="form-group">
                    <label for="writerPhoneNumber">{{$t('referrallead.form.writer.phone')}}</label>
                    <input v-model="vm.form.writerPhoneNumber" id="writerPhoneNumber" type="tel" />
                </div>
            </section>

            <section>
                <h2 class="section-title">{{$t('referrallead.form.lead.header')}}</h2>

                <div class="form-group">
                    <label for="leadCompanyName">{{$t('referrallead.form.lead.company.name')}}</label>
                    <input v-model="vm.form.leadCompanyName" id="leadCompanyName" type="text" required />
                </div>

                <div class="form-group">
                    <label for="leadContactFirstName">{{$t('referrallead.form.lead.contact.firstname')}}</label>
                    <input v-model="vm.form.leadContactFirstName" id="leadContactFirstName" type="text" />
                </div>

                <div class="form-group">
                    <label for="leadContactLastName">{{$t('referrallead.form.lead.contact.lastname')}}</label>
                    <input v-model="vm.form.leadContactLastName" id="leadContactLastName" type="text" />
                </div>

                <div class="form-group">
                    <label for="leadContactEmail">{{$t('referrallead.form.lead.contact.email')}}</label>
                    <input v-model="vm.form.leadContactEmail" id="leadContactEmail" type="email" />
                </div>

                <div class="form-group">
                    <label for="leadContactPhoneNumber">{{$t('referrallead.form.lead.contact.phone')}}</label>
                    <input v-model="vm.form.leadContactPhoneNumber" id="leadContactPhoneNumber" type="tel" />
                </div>

                <div class="form-group">
                    <label for="leadDescription">{{$t('referrallead.form.lead.description')}}</label>
                    <textarea v-model="vm.form.leadDescription" id="leadDescription" rows="4"></textarea>
                </div>
            </section>

            <div class="form-actions">
                <button type="submit" :disabled="isSubmitting">
                    <span v-if="isSubmitting">{{$t('referrallead.form.button.send')}}...</span>
                    <span v-else>{{$t('referrallead.form.button.send')}}</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script lang="ts">

    import BaseComponent from "@/components/base/baseComponent.vue";
    import { IReferralLeadFormPageViewModel } from "../../types/viewModels/commerce/referralLeadFormPageViewModel";
    import { Component, Prop } from "vue-facing-decorator";
    import { PropType } from "vue";
    import { Log } from "@/types/helpers/logHelper";

    @Component({
        components: {
            BaseComponent,
        },
    })
    export default class ReferralLead extends BaseComponent {
        @Prop({ type: Object as PropType<IReferralLeadFormPageViewModel>, required: true, default: () => ({ form: {} }) })
        vm!: IReferralLeadFormPageViewModel;

        isSubmitting = false;

        handleSubmit() {

            console.log("Form sent:", this.vm.form);
            this.isSubmitting = true;
            this.loadingStore.showSpinner("loading");
            this.axios
                .post("/api/ReferralLead/", this.vm.form)
                .then(() => {
                    this.$toast.add({
                        severity: "success",
                        summary: this.$t("referrallead.form.toast.success.summary"),
                        detail: this.$t("referrallead.form.toast.success.detail"),
                        life: 3000,
                    });
                })
                .catch((err) => {
                    Log.error(err)
                    this.$toast.add({
                        severity: "error",
                        summary: this.$t("referrallead.form.toast.error.summary"),
                        detail: this.$t("referrallead.form.toast.error.detail"),
                        life: 3000,
                    });
                })
	            .finally(() => {
                    this.loadingStore.hideSpinner("loading");
                    this.isSubmitting = false;
	            });
        }
    }

</script>

<style scoped lang="scss">
    .container {
        padding: 2rem 1rem;
        margin: 0 auto;
        background-color: #fff;
        border-radius: 0.5rem;
        box-shadow: 0 0.125rem 0.75rem rgba(0, 0, 0, 0.05);
        max-width: 1200px;
    }

    h1 {
        font-size: 1.875rem;
        color: #2c3e50;
        text-align: center;
        margin-bottom: 2.5rem;
    }

    form {
        display: flex;
        flex-wrap: wrap;
        gap: 2rem;
        justify-content: space-between;
    }

    section {
        flex: 1 1 45%;
        display: flex;
        flex-direction: column;
        background-color: #fafafa;
        border: 1px solid #eee;
        border-radius: 0.5rem;
        padding: 1.5rem;
        box-sizing: border-box;
        min-width: 320px;
        @media (min-width: 992px) {
            min-height: 100%;
        }
    }

    .section-title {
        font-size: 1.25rem;
        color: #91004b;
        border-left: 4px solid #91004b;
        padding-left: 0.5rem;
        margin-bottom: 1.5rem;
        font-weight: 600;
    }

    /* Formgroup */
    .form-group {
        display: flex;
        flex-direction: column;
        margin-bottom: 1.25rem;

        label {
            font-weight: 500;
            margin-bottom: 0.5rem;
            color: #444;
        }

        input,
        textarea {
            padding: 0.75rem 1rem;
            font-size: 1rem;
            border: 1px solid #ccc;
            border-radius: 0.375rem;
            transition: border-color 0.3s ease, box-shadow 0.3s ease;
        }

        input:focus,
        textarea:focus {
            border-color: #91004b;
            box-shadow: 0 0 0 3px rgba(145, 0, 75, 0.15);
            outline: none;
        }

        textarea {
            resize: vertical;
            min-height: 6rem;
        }
    }

    /* Submit button */
    .form-actions {
        width: 100%;
        margin-top: 2rem;
        display: flex;
        justify-content: flex-end;

        button[type="submit"] {
            background-color: #91004b;
            color: white;
            padding: 0.75rem 2rem;
            font-size: 1rem;
            font-weight: 600;
            border: none;
            border-radius: 0.375rem;
            cursor: pointer;
            transition: background-color 0.3s ease;

            &:hover {
                background-color: #c91254;
            }
        }
    }

    /* Mobile responsiveness to size up column width on mobile */
    @media (max-width: 768px) {
        form {
            flex-direction: column;
        }

        section {
            width: 100%;
        }
    }
</style>